import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { Dumbbell, Clock, CheckCircle2, Lock, ChevronDown, ChevronUp, Unlock, Eye, Wind, BrainCircuit, MessageSquare, Compass, Heart, Feather, Sparkles, Rocket, Star } from "lucide-react";

// Block definitions with icons (white/subtle)
const BLOCKS_CONFIG = {
  1: { icon: Eye, title_es: "Consciencia Emocional", title_ca: "Consciència Emocional", desc_es: "Empieza a observar tus emociones", desc_ca: "Comença a observar les teves emocions" },
  2: { icon: Wind, title_es: "Respiración y Presencia", title_ca: "Respiració i Presència", desc_es: "Conecta con el momento presente", desc_ca: "Conecta amb el moment present" },
  3: { icon: BrainCircuit, title_es: "Observar Pensamientos", title_ca: "Observar Pensaments", desc_es: "Entiende cómo funciona tu mente", desc_ca: "Entén com funciona la teva ment" },
  4: { icon: MessageSquare, title_es: "Diálogo Interior", title_ca: "Diàleg Interior", desc_es: "Comunícate con tus partes internas", desc_ca: "Comunica't amb les teves parts internes" },
  5: { icon: Compass, title_es: "El GPS Emocional", title_ca: "El GPS Emocional", desc_es: "Las emociones como guía", desc_ca: "Les emocions com a guia" },
  6: { icon: Heart, title_es: "Autoamor y Compasión", title_ca: "Autoamor i Compassió", desc_es: "Cuídate desde el amor", desc_ca: "Cuida't des de l'amor" },
  7: { icon: Feather, title_es: "Liberación y Sanación", title_ca: "Alliberament i Sanació", desc_es: "Suelta lo que ya no sirve", desc_ca: "Deixa anar el que ja no serveix" },
  8: { icon: Sparkles, title_es: "Conexión Universal", title_ca: "Connexió Universal", desc_es: "Siente tu conexión con el todo", desc_ca: "Sent la teva connexió amb el tot" },
  9: { icon: Rocket, title_es: "Límites y Expansión", title_ca: "Límits i Expansió", desc_es: "Crece con consciencia", desc_ca: "Creix amb consciència" },
  10: { icon: Star, title_es: "Integración y Propósito", title_ca: "Integració i Propòsit", desc_es: "Tu camino único", desc_ca: "El teu camí únic" }
};

const Exercises = () => {
  const { language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, unlocked: 0, blocksCompleted: 0, totalBlocks: 0 });
  const [loading, setLoading] = useState(true);
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [lockedExercises, setLockedExercises] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/exercises/blocks`, { headers });
      
      setBlocks(response.data.blocks);
      const blocksData = response.data.blocks;
      const blocksCompleted = blocksData.filter(b => b.completed).length;
      setStats({
        total: response.data.total_exercises,
        completed: response.data.completed_count,
        unlocked: response.data.unlocked_count,
        blocksCompleted: blocksCompleted,
        totalBlocks: blocksData.length
      });
      
      // Collect all locked exercises for the unlock modal
      const locked = [];
      response.data.blocks.forEach(block => {
        block.exercises.forEach(ex => {
          if (!ex.unlocked) {
            locked.push({ ...ex, block_number: block.block_number });
          }
        });
      });
      setLockedExercises(locked);
      
      // All blocks start closed
      setExpandedBlocks({});
    } catch (error) {
      console.error("Error fetching exercises:", error);
      toast.error("Error al cargar ejercicios");
    } finally {
      setLoading(false);
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;

  const toggleBlock = (blockNumber) => {
    setExpandedBlocks(prev => ({
      ...prev,
      [blockNumber]: !prev[blockNumber]
    }));
  };

  const handleExerciseClick = (exercise) => {
    if (!exercise.unlocked) {
      toast.info(t(
        "Completa un ejercicio para desbloquear este",
        "Completa un exercici per desbloquejar aquest"
      ));
      return;
    }
    navigate(`/exercises/${exercise.id}`);
  };

  const handleUnlockExercise = async (exerciseId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/exercises/unlock/${exerciseId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("¡Ejercicio desbloqueado!", "Exercici desbloquejat!"));
      setShowUnlockModal(false);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error unlocking exercise:", error);
      toast.error(error.response?.data?.detail || t("Error al desbloquear", "Error en desbloquejar"));
    }
  };

  const canUnlockMore = stats.completed > 0 && stats.unlocked < stats.total;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-pulse text-teal-300 text-xl">{t("Cargando...", "Carregant...")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <Navigation />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div data-testid="exercises-header" className="mb-8 fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 glow-text">
            {t("Ejercicios", "Exercicis")}
          </h1>
          <p className="text-lg text-gray-300 mb-4">
            {t("Herramientas para tu práctica diaria", "Eines per a la teva pràctica diària")}
          </p>
          
          {/* Progress Stats */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="glass rounded-xl px-4 py-2 border border-teal-400/20">
              <span className="text-gray-400">{t("Ejercicios", "Exercicis")}: </span>
              <span className="text-teal-300 font-semibold">{stats.completed}/{stats.total}</span>
            </div>
            <div className="glass rounded-xl px-4 py-2 border border-teal-400/20">
              <span className="text-gray-400">{t("Bloques", "Blocs")}: </span>
              <span className="text-teal-300 font-semibold">{stats.blocksCompleted}/{stats.totalBlocks}</span>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="glass rounded-xl p-4 border border-teal-400/10 mb-8 fade-in" style={{animationDelay: '0.05s'}}>
          <p className="text-gray-300 text-sm">
            <span className="text-teal-300">💡</span>{" "}
            {t(
              "Completa un ejercicio para desbloquear uno nuevo. Repítelos siempre que quieras y elige el que sientas que necesitas en cada momento.",
              "Completa un exercici per desbloquejar-ne un de nou. Repeteix-los cada vegada que ho vulguis i tria el que sentis que necessites a cada moment."
            )}
          </p>
        </div>

        {/* Unlock Button */}
        {canUnlockMore && lockedExercises.length > 0 && (
          <button
            onClick={() => setShowUnlockModal(true)}
            className="mb-6 btn-secondary flex items-center gap-2 border-teal-400/30 hover:border-teal-400/50"
          >
            <Unlock className="w-5 h-5 text-teal-300" />
            {t("Desbloquear nuevo ejercicio", "Desbloquejar nou exercici")}
          </button>
        )}

        {/* Blocks List */}
        <div data-testid="blocks-list" className="space-y-4">
          {blocks.map((block, blockIndex) => {
            const config = BLOCKS_CONFIG[block.block_number] || {};
            const isExpanded = expandedBlocks[block.block_number];
            const hasUnlocked = block.exercises.some(e => e.unlocked);
            const completedCount = block.exercises.filter(e => e.completed).length;
            const isBlockCompleted = completedCount === block.exercises.length && block.exercises.length > 0;
            const isLocked = !hasUnlocked;
            
            return (
              <div
                key={block.block_number}
                data-testid={`block-${block.block_number}`}
                className={`card glass rounded-2xl overflow-hidden fade-in transition-all ${
                  isLocked
                    ? 'border-gray-700/30 opacity-70'
                    : isBlockCompleted
                      ? 'border-teal-400/20'
                      : 'border-purple-400/20'
                }`}
                style={{ animationDelay: `${blockIndex * 0.08}s` }}
              >
                {/* Block Header */}
                <div 
                  className={`p-5 cursor-pointer flex items-center justify-between gap-4 ${isLocked ? '' : 'hover:bg-white/5'}`}
                  onClick={() => toggleBlock(block.block_number)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl ${
                      isLocked
                        ? 'bg-gray-800/50'
                        : isBlockCompleted
                          ? 'bg-gradient-to-br from-teal-500/30 to-teal-700/30'
                          : 'bg-gradient-to-br from-purple-500/30 to-purple-700/30'
                    }`}>
                      {isLocked
                        ? <Lock className="w-6 h-6 text-gray-500" />
                        : (config.icon && <config.icon className="w-6 h-6 text-gray-300" />)
                      }
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          isLocked
                            ? 'bg-gray-700 text-gray-400'
                            : isBlockCompleted
                              ? 'bg-teal-500/20 text-teal-300'
                              : 'bg-purple-500/20 text-purple-300'
                        }`}>
                          {t("Bloque", "Bloc")} {block.block_number}
                        </span>
                        {isBlockCompleted && (
                          <CheckCircle2 className="w-4 h-4 text-teal-400" />
                        )}
                      </div>
                      <h3 className={`text-lg font-semibold mt-1 ${isLocked ? 'text-gray-400' : 'text-white'}`}>
                        {language === "ca" ? config.title_ca : config.title_es}
                      </h3>
                      <p className={`text-sm ${hasUnlocked ? 'text-gray-400' : 'text-gray-500'}`}>
                        {language === "ca" ? config.desc_ca : config.desc_es}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className="text-sm text-gray-400">
                        {completedCount}/{block.exercises.length}
                      </span>
                    </div>
                    {isExpanded 
                      ? <ChevronUp className="w-5 h-5 text-gray-400" />
                      : <ChevronDown className="w-5 h-5 text-gray-400" />
                    }
                  </div>
                </div>

                {/* Block Exercises (Expanded) */}
                {isExpanded && (
                  <div className="border-t border-slate-700/50 bg-slate-900/30">
                    {block.exercises.map((exercise, exIndex) => (
                      <div
                        key={exercise.id}
                        data-testid={`exercise-${exercise.id}`}
                        className={`p-4 border-b border-slate-700/30 last:border-b-0 transition-colors ${
                          exercise.unlocked 
                            ? 'hover:bg-white/5 cursor-pointer' 
                            : 'opacity-50'
                        }`}
                        onClick={() => handleExerciseClick(exercise)}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                            exercise.completed 
                              ? 'bg-teal-500/30 text-teal-300' 
                              : exercise.unlocked
                                ? 'bg-teal-500/30 text-teal-300'
                                : 'bg-slate-700 text-gray-500'
                          }`}>
                            {!exercise.unlocked ? (
                              <Lock className="w-3 h-3" />
                            ) : exercise.completed ? (
                              <CheckCircle2 className="w-4 h-4" />
                            ) : (
                              exIndex + 1
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-medium ${
                              exercise.completed 
                                ? 'text-teal-200' 
                                : exercise.unlocked 
                                  ? 'text-gray-200'
                                  : 'text-gray-500'
                            }`}>
                              {language === "ca" ? exercise.title_ca : exercise.title_es}
                            </h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Clock className="w-3 h-3 text-gray-500" />
                              <span className="text-xs text-gray-500">{exercise.duration_minutes || 15} min</span>
                              {exercise.completed && (
                                <span className="text-xs text-teal-400 ml-2">
                                  {t("Completado", "Completat")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Unlock Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-slate-700">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <Unlock className="w-5 h-5 text-teal-300" />
                {t("Elige qué ejercicio desbloquear", "Tria quin exercici desbloquejar")}
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                {t(
                  "Selecciona el ejercicio que sientas que necesitas ahora",
                  "Selecciona l'exercici que sentis que necessites ara"
                )}
              </p>
            </div>
            <div className="overflow-y-auto max-h-[50vh] p-4 space-y-2">
              {lockedExercises.map((exercise) => {
                const blockConfig = BLOCKS_CONFIG[exercise.block_number] || {};
                return (
                  <button
                    key={exercise.id}
                    onClick={() => handleUnlockExercise(exercise.id)}
                    className="w-full p-4 rounded-xl bg-slate-800/50 hover:bg-teal-500/20 border border-slate-700 hover:border-teal-400/50 transition-all text-left"
                  >
                    <div className="flex items-center gap-3">
                      {blockConfig.icon && <blockConfig.icon className="w-5 h-5 text-gray-400" />}
                      <div>
                        <p className="text-white font-medium">
                          {language === "ca" ? exercise.title_ca : exercise.title_es}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t("Bloque", "Bloc")} {exercise.block_number}: {language === "ca" ? blockConfig.title_ca : blockConfig.title_es}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="p-4 border-t border-slate-700">
              <button
                onClick={() => setShowUnlockModal(false)}
                className="w-full btn-secondary"
              >
                {t("Cancelar", "Cancel·lar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Exercises;
